import { computed, Signal } from '@preact/signals-react';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { CompoundChange } from '@/pkg/sdk';
import { AiComponentJsVariable, JavascriptVariableType } from '@/pkg/ai';

export function useJavascriptManipulator(
  change: Signal<EditorDeclarativeBlock>,
  setJs: (js: string) => void,
) {
  const js = computed(() => {
    const block = change.value.block.value as CompoundChange;
    return block.js || ``;
  });

  const jsVariables = computed(() => {
    const code = js.value;
    const variableRegex =
      /const\s+(_vslyExposed_[a-zA-Z0-9_]+)\s*=\s*(.+?)(?:;|$)/g;
    const variables: AiComponentJsVariable[] = [];

    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = variableRegex.exec(code)) !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, variable, value] = match;
      let variableType: JavascriptVariableType;

      if (/^".*"$/.test(value) || /^'.*'$/.test(value)) {
        variableType = `string`;
      } else if (/^-?\d+(\.\d+)?$/.test(value)) {
        variableType = `number`;
      } else if (/^(true|false)$/.test(value)) {
        variableType = `boolean`;
      } else {
        // eslint-disable-next-line no-continue
        continue;
      }

      variables.push({
        type: `js-variable`,
        name: variable,
        description: ``,
        selector: ``,
        variableType,
        value: value.replace(/^['"]|['"]$/g, ``),
        variable,
      });
    }

    return variables;
  });

  const manifestJsVariables = computed(() => {
    const vars = jsVariables.value;
    const manifest = (change.value.block.value as CompoundChange).editManifest;
    return manifest?.components
      ?.filter((c) => c.type === `js-variable`)
      ?.map((c: AiComponentJsVariable) => {
        const v = vars.find((v) => v.variable === c.variable);
        return {
          ...c,
          value: v?.value,
          variableType: v?.variableType || c.variableType,
        };
      });
  });

  const getVariables = (): AiComponentJsVariable[] => jsVariables.value;

  const getManifestVariables = (): AiComponentJsVariable[] =>
    manifestJsVariables.value || [];

  const setVariable = (variable: AiComponentJsVariable) => {
    const code = js.value;
    const variableRegex = new RegExp(
      `const\\s+${variable.variable}\\s*=\\s*.+`,
    );

    let casted: any = variable.value;
    if (variable.variableType === `number`) {
      casted = parseFloat(variable.value);
    } else if (variable.variableType === `boolean`) {
      casted = variable.value === `true`;
    }

    const newCode = code.replace(
      variableRegex,
      `const ${variable.variable} = ${JSON.stringify(casted)};`,
    );

    setJs(newCode);
  };

  return {
    getVariables,
    setVariable,
    getManifestVariables,
    jsVariables,
    parsedJs: js,
  };
}
